import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";
import { pluginsKeys } from "@/hooks/api/use-plugins";

export enum ServiceType {
	REALTIME = "realtime",
	CALENDAR = "calendar",
	TTS = "tts",
	LLM = "llm",
	STT = "stt",
	VOICE = "voice",
	EMAIL = "email",
	WEBHOOK = "webhook",
	WEBSOCKET = "websocket",
	EMBEDDING = "embedding",
  AISDK = "ai-sdk",
}

export interface Service {
	id: string;
	title: string;
	type: ServiceType;
	plugin: {
		id: string;
		enabled: boolean;
		title: string;
		plugin: {
			name: string;
			title: string;
		};
	};
	settings: Record<string, any>;
}

export interface ServicesResponse {
	data: Service[];
	total: number;
}

export const servicesKeys = {
	all: ["services"] as const,
	lists: () => [...servicesKeys.all, "list"] as const,
	list: (query?: string) => [...servicesKeys.lists(), { query }] as const,
	details: () => [...servicesKeys.all, "detail"] as const,
	detail: (id: string) => [...servicesKeys.details(), id] as const,
};

export function useServicesList(query?: string) {
	return useQuery({
		queryKey: servicesKeys.list(query),
		queryFn: async (): Promise<ServicesResponse> => {
			const queryString = query ? `?${query}` : "";
			const response = await apiClient.get(`/services${queryString}`);
			return response.data;
		},
    staleTime: 0, 
    refetchOnMount: true, 
    refetchOnWindowFocus: true, 
	});
}

export function useService(id: string) {
	return useQuery({
		queryKey: servicesKeys.detail(id),
		queryFn: async (): Promise<Service> => {
			const response = await apiClient.get(`/services/${id}`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useCreateService() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: Service): Promise<Service> => {
			const response = await apiClient.post("/services", data);
			return response.data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: servicesKeys.lists() });
			queryClient.invalidateQueries({ queryKey: pluginsKeys.services(data.type) });
			toast.success("Service created successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Something went wrong!");
		},
	});
}

export function useUpdateService() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Service }): Promise<Service> => {
			const response = await apiClient.put(`/services/${id}`, data);
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(servicesKeys.detail(variables.id), data);
			queryClient.invalidateQueries({ queryKey: servicesKeys.lists() });
			queryClient.invalidateQueries({ queryKey: pluginsKeys.services(data.type) });
			toast.success("Service updated successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Something went wrong!");
		},
	});
}

export function useDeleteService() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<void> => {
			await apiClient.delete(`/services/${id}`);
		},
		onSuccess: (_, id) => {
			queryClient.removeQueries({ queryKey: servicesKeys.detail(id) });
			queryClient.invalidateQueries({ queryKey: servicesKeys.lists() });
			toast.success("Service deleted successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error("Something went wrong!");
		},
	});
}
